import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const MarketInternalsContext = createContext();

export const useMarketInternalsData = () => useContext(MarketInternalsContext);

export const MarketInternalsProvider = ({ children }) => {
    const [rows, setRows] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`${API_URL}/market-internals`);
                if (response.data) {
                    setRows(response.data);
                }
            } catch (error) {
                console.error("Error fetching market internals data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const updateRow = async (index, updatedRow) => {
        const rowToUpdate = rows[index];
        if (!rowToUpdate.id) {
            console.error("Cannot update row without ID");
            return;
        }

        try {
            // Optimistic update
            const newRows = [...rows];
            newRows[index] = updatedRow;
            setRows(newRows);

            await axios.put(`${API_URL}/market-internals/${rowToUpdate.id}`, {
                category: updatedRow.Category,
                topic: updatedRow.Topic,
                subcategory: updatedRow.Subcategory,
                catalyst: updatedRow.Catalyst,
                tag: updatedRow.Tag,
                headline: updatedRow.Headline,
                date: updatedRow.Date,
                interpretation: updatedRow.Interpretation,
                impact: updatedRow.Impact,
                assets: updatedRow.Assets,
                sources: updatedRow.Sources
            });
        } catch (error) {
            console.error("Error updating market internal event:", error);
            // Revert on error (could be improved)
            window.location.reload();
        }
    };

    const deleteRow = async (index) => {
        const rowToDelete = rows[index];
        if (!rowToDelete.id) {
            console.error("Cannot delete row without ID");
            return;
        }

        try {
            // Optimistic update
            setRows(prev => prev.filter((_, i) => i !== index));
            await axios.delete(`${API_URL}/market-internals/${rowToDelete.id}`);
        } catch (error) {
            console.error("Error deleting market internal event:", error);
            window.location.reload();
        }
    };

    const addRow = async (initialData = {}) => {
        const newRowData = {
            category: initialData.Category || "New Category",
            topic: initialData.Topic || null,
            subcategory: initialData.Subcategory || null,
            catalyst: initialData.Catalyst || "New Catalyst",
            tag: initialData.Tag || null,
            headline: initialData.Headline || "New Headline",
            date: initialData.Date || new Date().toISOString().split('T')[0],
            interpretation: initialData.Interpretation || "New Interpretation",
            impact: initialData.Impact || "Low",
            assets: initialData.Assets || "EUR/USD",
            sources: initialData.Sources || "Source"
        };

        try {
            const response = await axios.post(`${API_URL}/market-internals`, newRowData);
            if (response.data && response.data.id) {
                const newRow = {
                    id: response.data.id,
                    Category: newRowData.category,
                    Topic: newRowData.topic, // Mapped topic to Topic for local state
                    Subcategory: newRowData.subcategory,
                    Catalyst: newRowData.catalyst,
                    Tag: newRowData.tag,
                    Headline: newRowData.headline,
                    Date: newRowData.date,
                    Interpretation: newRowData.interpretation,
                    Impact: newRowData.impact,
                    Assets: newRowData.assets,
                    Sources: newRowData.sources
                };
                setRows(prev => [...prev, newRow]);
            }
        } catch (error) {
            console.error("Error creating market internal event:", error);
        }
    };

    const resetData = async () => {
        setIsLoading(true);
        try {
            // Artificial delay for UX feedback
            await new Promise(resolve => setTimeout(resolve, 400));
            const response = await axios.get(`${API_URL}/market-internals`);
            if (response.data) {
                setRows(response.data);
            }
        } catch (error) {
            console.error("Error resetting data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper to find the original index since tabs filter the data
    const getOriginalIndex = (row) => {
        return rows.findIndex(r => r.id === row.id);
    };

    const setAllData = (newRows) => {
        setRows(newRows);
    };

    const reorderRows = (startIndex, endIndex) => {
        setRows(prev => {
            const result = Array.from(prev);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        });
    };

    const [impactFilter, setImpactFilter] = useState('All');

    const filteredRows = React.useMemo(() => {
        if (impactFilter === 'All') return rows;

        return rows.filter(row => {
            if (!row.Impact) return false;
            const impact = row.Impact;

            switch (impactFilter) {
                case 'Very High':
                    return impact.includes('Very High');
                case 'High':
                    return impact.includes('High');
                case 'Medium':
                    return impact.includes('Medium');
                case 'Low':
                    return impact.includes('Low');
                default:
                    return true;
            }
        });
    }, [rows, impactFilter]);

    return (
        <MarketInternalsContext.Provider value={{
            rows,
            filteredRows,
            impactFilter,
            setImpactFilter,
            updateRow,
            deleteRow,
            addRow,
            getOriginalIndex,
            setAllData,
            resetData,
            reorderRows,
            isLoading
        }}>
            {children}
        </MarketInternalsContext.Provider>
    );
};
